
import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import { PlusCircle, Edit, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../../AxiosConfig";

export default function Receipt() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // =========================
  // Fetch Receipt Data
  // =========================
  const fetchReceipts = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("userData"));
      const db = user?.database;

      const res = await axiosConfig.get(
        `/receipt/view-reciept/${db}`
      );

      const receipts = Array.isArray(res?.data?.Receipts)
        ? res.data.Receipts
        : [];

      const filtered = receipts
        .filter(
          (ele) =>
            ele?.type?.toLowerCase() === "receipt" &&
            ele?.status?.toLowerCase() === "active"
        )
        .sort(
          (a, b) =>
            new Date(b.date) - new Date(a.date)
        );

      setRowData(filtered);
    } catch (err) {
      console.log("RECEIPT GET ERROR =>", err);
      console.log("ERROR RESPONSE =>", err?.response?.data);

      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  // =========================
  // Delete Receipt
  // =========================
  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Are you sure?",
      text: "You want to delete this receipt?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (!confirm) return;

    try {
      setLoading(true);

      await axiosConfig.delete(
        `/receipt/delete-receipt/${id}`
      );

      await swal(
        "Deleted!",
        "Receipt deleted successfully!",
        "success"
      );

      // Refresh table
      await fetchReceipts();
    } catch (err) {
      console.log("DELETE RECEIPT ERROR =>", err);
      console.log("ERROR RESPONSE =>", err?.response?.data);

      await swal(
        "Error",
        "Unable to delete receipt!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Particular Name
  // =========================
  const getParticular = (item) => {
    if (item?.expenseId) {
      return item?.expenseId?.title || "NA";
    }

    if (item?.partyId) {
      return item?.partyId?.CompanyName || "NA";
    }

    if (item?.userId) {
      return item?.userId?.firstName || "NA";
    }

    if (
      item?.paymentMode === "Bank" &&
      item?.bankDetails?.length > 0
    ) {
      return item?.bankDetails[0]?.bankName || "NA";
    }

    return "NA";
  };

  return (
    <div className="erp-page-grid">

      {/* ================= HEADER ================= */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <h2 style={{ margin: 0 }}>
          Receipt
        </h2>

        <motion.button
          className="erp-btn erp-btn-primary"
          onClick={() =>
            navigate(
              "/transactions?view=create-receipt"
            )
          }
          whileTap={{ scale: 0.97 }}
        >
          <PlusCircle size={15} />
          Add Receipt
        </motion.button>
      </div>

      {/* ================= TABLE CARD ================= */}
      <motion.div
        className="erp-card"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="erp-card-head">
          <div>
            <h3>Receipts</h3>
            <p>
              Manage all receipt transactions
            </p>
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div
          className="erp-table-wrap"
          style={{
            overflowX: "auto",
          }}
        >
          <table className="erp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Particular</th>
                <th>Amount</th>
                <th>Payment Mode</th>
                <th>Remark</th>
                <th style={{ textAlign: "center" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "25px",
                    }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rowData.length > 0 ? (
                rowData.map((item, index) => (
                  <motion.tr
                    key={item?._id || index}
                    initial={{
                      opacity: 0,
                    }}
                    animate={{
                      opacity: 1,
                    }}
                    transition={{
                      delay: index * 0.03,
                    }}
                  >
                    {/* # */}
                    <td>
                      {index + 1}
                    </td>

                    {/* Date */}
                    <td>
                      {item?.date
                        ? item.date.split("T")[0]
                        : "NA"}
                    </td>

                    {/* Particular */}
                    <td>
                      {getParticular(item)}
                    </td>

                    {/* Amount */}
                    <td>
                      ₹{item?.amount || 0}
                    </td>

                    {/* Payment Mode */}
                    <td>
                      {item?.paymentMode || "NA"}
                    </td>

                    {/* Remark */}
                    <td>
                      {item?.remark || ""}
                    </td>

                    {/* Action */}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {/* Edit */}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit Receipt"
                          onClick={() =>
                            navigate(
                              `/transactions?view=create-receipt-${item?._id}`
                            )
                          }
                        >
                          <Edit size={15} />
                        </button>

                        {/* Delete */}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete Receipt"
                          onClick={() =>
                            handleDelete(item?._id)
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "25px",
                    }}
                  >
                    No receipts available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
